// Head-to-Head panel → shareable PNG report card. The panel is DOM tables, so
// rather than screenshotting we redraw the same derived data onto a canvas:
// title, scoreboard, per-corner time table (diverging bars + badges), the
// badged corners expanded with entry/exit splits + technique tables, signature
// strip. Width fixed at 1920; height grows with content.

import { EXPORT_FONT, resolveBackground, drawBadge, downloadCanvasPng } from './chartExport';

const W = 1920;
const PAD = 28;
const LOSS = '#e05252';
const GAIN = '#44cc88';
const TEXT = '#c9d4dd';
const BRIGHT = '#e8edf2';
const MUTED = '#8b98a5';
const BORDER = 'rgba(139,152,165,0.25)';
const DETAIL_BG = 'rgba(139,152,165,0.07)';
const ANNOT_YELLOW = '#f5c842';

const ROW = 34;
const LABEL_W = 230;
const BAR_W = 170;

function chip(ctx, x, y, color, size = 13) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.roundRect(x, y - size / 2, size, size, 3);
  ctx.fill();
}

function deltaColor(v) {
  if (v == null) return MUTED;
  return v > 0.05 ? LOSS : v < -0.05 ? GAIN : MUTED;
}

function drawBar(ctx, x, y, value, maxAbs) {
  ctx.fillStyle = 'rgba(139,152,165,0.15)';
  ctx.beginPath();
  ctx.roundRect(x, y - 5, BAR_W, 10, 2);
  ctx.fill();
  if (value == null) return;
  const frac = Math.min(1, Math.abs(value) / maxAbs);
  const half = BAR_W / 2;
  ctx.fillStyle = deltaColor(value);
  if (value >= 0) ctx.fillRect(x + half, y - 5, half * frac, 10);
  else ctx.fillRect(x + half - half * frac, y - 5, half * frac, 10);
}

/**
 * All content arrives display-ready from HeadToHead.jsx:
 *  scoreboard: [{ name, accent, lapLabel, gapLabel, isRef }]
 *  columns:    [{ key, name, accent }]                      (non-ref drivers)
 *  rows:       [{ label, cells: [{ text, value, badge }],
 *                 detail?: { entryExit: [{ name, accent, entryText, entryVal, exitText, exitVal }],
 *                            techHeaders: [{ label, unit }],
 *                            techRows: [{ name, accent, cells: [{ text, diffText, good }] }] } }]
 *  straights:  { label, cells: [{ text, value }] }
 *  signature:  [{ name, accent, stats: [{ label, value }] }]
 */
export async function exportHeadToHeadPng({
  title, subtitle, watermark, themeFrom,
  refName, scoreboard, columns, rows, straights, maxAbs, signature, filename,
}) {
  const bg = resolveBackground(themeFrom);
  const colW = (W - PAD * 2 - LABEL_W) / Math.max(1, columns.length);

  const SPARK_H = 110;
  const hasSpark = (d) => (d.spark?.length ?? 0) >= 2;
  const detailH = (d) => 30 + (hasSpark(d) ? SPARK_H : 0) + 30 + d.techRows.length * 30 + 14;
  const height =
    64 +                                        // title band
    scoreboard.length * 30 + 18 +               // scoreboard
    34 +                                        // table header
    rows.reduce((h, r) => h + ROW + (r.detail ? detailH(r.detail) : 0), 0) +
    ROW + 12 +                                  // straights row
    12 + signature.length * 30 +                // signature strip
    46;                                         // footer / watermark

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, height);
  ctx.textBaseline = 'middle';

  // Title
  ctx.fillStyle = BRIGHT;
  ctx.font = `600 24px ${EXPORT_FONT}`;
  ctx.fillText(title, PAD, 26);
  if (subtitle) {
    ctx.fillStyle = MUTED;
    ctx.font = `14px ${EXPORT_FONT}`;
    ctx.fillText(subtitle, PAD, 50);
  }

  let y = 64;

  // Scoreboard
  for (const s of scoreboard) {
    y += 30;
    chip(ctx, PAD, y - 15, s.accent);
    ctx.fillStyle = BRIGHT;
    ctx.font = `600 17px ${EXPORT_FONT}`;
    ctx.fillText(s.name, PAD + 24, y - 15);
    ctx.fillStyle = MUTED;
    ctx.font = `16px ${EXPORT_FONT}`;
    ctx.fillText(s.lapLabel, PAD + 240, y - 15);
    if (s.isRef) {
      ctx.strokeStyle = '#00cfff';
      ctx.fillStyle = '#00cfff';
      ctx.font = `600 12px ${EXPORT_FONT}`;
      const w = ctx.measureText('REF').width + 12;
      ctx.strokeRect(PAD + 470, y - 24, w, 18);
      ctx.fillText('REF', PAD + 476, y - 14.5);
    } else {
      ctx.fillStyle = LOSS;
      ctx.font = `16px ${EXPORT_FONT}`;
      ctx.fillText(s.gapLabel, PAD + 470, y - 15);
    }
  }
  y += 18;

  // Table header
  y += 34;
  ctx.strokeStyle = BORDER;
  ctx.beginPath(); ctx.moveTo(PAD, y - 8); ctx.lineTo(W - PAD, y - 8); ctx.stroke();
  ctx.fillStyle = MUTED;
  ctx.font = `600 13px ${EXPORT_FONT}`;
  ctx.fillText(`TIME VS ${refName.toUpperCase()}`, PAD, y - 22);
  columns.forEach((c, i) => {
    const x = PAD + LABEL_W + i * colW;
    chip(ctx, x, y - 22, c.accent, 11);
    ctx.fillStyle = MUTED;
    ctx.fillText(c.name.toUpperCase(), x + 18, y - 22);
  });

  // Corner rows (+ expanded details)
  for (const r of rows) {
    y += ROW;
    ctx.fillStyle = TEXT;
    ctx.font = `16px ${EXPORT_FONT}`;
    ctx.fillText(r.label, PAD, y - ROW / 2);
    r.cells.forEach((cell, i) => {
      const x = PAD + LABEL_W + i * colW;
      drawBar(ctx, x, y - ROW / 2, cell.value, maxAbs);
      ctx.fillStyle = deltaColor(cell.value);
      ctx.font = `16px ${EXPORT_FONT}`;
      ctx.fillText(cell.text, x + BAR_W + 12, y - ROW / 2);
      if (cell.badge) drawBadge(ctx, x + BAR_W + 90, y - ROW / 2, cell.badge, 9);
    });
    ctx.strokeStyle = BORDER;
    ctx.beginPath(); ctx.moveTo(PAD, y); ctx.lineTo(W - PAD, y); ctx.stroke();

    if (r.detail) {
      const d = r.detail;
      const blockH = detailH(d);
      ctx.fillStyle = DETAIL_BG;
      ctx.fillRect(PAD, y, W - PAD * 2, blockH);

      // entry/exit line
      y += 30;
      let ex = PAD + 14;
      ctx.font = `15px ${EXPORT_FONT}`;
      for (const item of d.entryExit) {
        chip(ctx, ex, y - 15, item.accent, 11); ex += 18;
        ctx.fillStyle = MUTED; ctx.fillText('entry ', ex, y - 15); ex += ctx.measureText('entry ').width;
        ctx.fillStyle = deltaColor(item.entryVal); ctx.fillText(item.entryText, ex, y - 15); ex += ctx.measureText(item.entryText).width;
        ctx.fillStyle = MUTED; ctx.fillText(' · exit ', ex, y - 15); ex += ctx.measureText(' · exit ').width;
        ctx.fillStyle = deltaColor(item.exitVal); ctx.fillText(item.exitText, ex, y - 15); ex += ctx.measureText(item.exitText).width + 34;
      }

      // speed profile through the corner (per driver, apex dashed at x = 0)
      if (hasSpark(d)) {
        const SW = 560, SH = SPARK_H - 20, sx = PAD + 50, sy = y + 6;
        const xs = d.spark.flatMap(s => s.pts.map(p => p[0]));
        const ys = d.spark.flatMap(s => s.pts.map(p => p[1]));
        const x0 = Math.min(...xs), x1 = Math.max(...xs);
        let y0 = Math.min(...ys), y1 = Math.max(...ys);
        if (y1 - y0 < 2) { y0 -= 1; y1 += 1; }
        const SX = v => sx + ((v - x0) / (x1 - x0 || 1)) * SW;
        const SY = v => sy + (1 - (v - y0) / (y1 - y0)) * SH;
        if (x0 < 0 && x1 > 0) {
          ctx.strokeStyle = ANNOT_YELLOW;
          ctx.globalAlpha = 0.5;
          ctx.setLineDash([3, 3]);
          ctx.beginPath(); ctx.moveTo(SX(0), sy); ctx.lineTo(SX(0), sy + SH); ctx.stroke();
          ctx.setLineDash([]);
          ctx.globalAlpha = 1;
          ctx.fillStyle = MUTED;
          ctx.font = `11px ${EXPORT_FONT}`;
          const prevAlign = ctx.textAlign;
          ctx.textAlign = 'center';
          ctx.fillText('apex', SX(0), sy + SH + 9);
          ctx.textAlign = prevAlign;
        }
        for (const s of d.spark) {
          ctx.strokeStyle = s.accent;
          ctx.lineWidth = 2;
          ctx.lineJoin = 'round';
          ctx.beginPath();
          s.pts.forEach((p, i) => i === 0 ? ctx.moveTo(SX(p[0]), SY(p[1])) : ctx.lineTo(SX(p[0]), SY(p[1])));
          ctx.stroke();
        }
        ctx.fillStyle = MUTED;
        ctx.font = `11px ${EXPORT_FONT}`;
        const pa = ctx.textAlign;
        ctx.textAlign = 'right';
        ctx.fillText(String(Math.round(y1)), sx - 6, SY(y1) + 2);
        ctx.fillText(String(Math.round(y0)), sx - 6, SY(y0));
        ctx.textAlign = pa;
        y += SPARK_H;
      }

      // technique table
      const nameW = 200;
      const techW = (W - PAD * 2 - 28 - nameW) / d.techHeaders.length;
      y += 30;
      ctx.font = `600 12px ${EXPORT_FONT}`;
      d.techHeaders.forEach((h, i) => {
        const x = PAD + 14 + nameW + i * techW;
        ctx.fillStyle = MUTED;
        const label = h.label.toUpperCase();
        ctx.fillText(label, x, y - 15);
        ctx.fillStyle = 'rgba(139,152,165,0.55)';
        ctx.fillText(` ${h.unit}`, x + ctx.measureText(label).width, y - 15);
      });
      for (const tr of d.techRows) {
        y += 30;
        chip(ctx, PAD + 14, y - 15, tr.accent, 11);
        ctx.fillStyle = TEXT;
        ctx.font = `15px ${EXPORT_FONT}`;
        ctx.fillText(tr.name, PAD + 32, y - 15);
        tr.cells.forEach((cell, i) => {
          const x = PAD + 14 + nameW + i * techW;
          ctx.fillStyle = TEXT;
          ctx.font = `15px ${EXPORT_FONT}`;
          ctx.fillText(cell.text, x, y - 15);
          if (cell.diffText) {
            ctx.fillStyle = cell.good == null ? MUTED : cell.good ? GAIN : LOSS;
            ctx.fillText(` ${cell.diffText}`, x + ctx.measureText(cell.text).width, y - 15);
          }
        });
      }
      y += 14;
      ctx.strokeStyle = BORDER;
      ctx.beginPath(); ctx.moveTo(PAD, y); ctx.lineTo(W - PAD, y); ctx.stroke();
    }
  }

  // Straights row
  y += ROW;
  ctx.fillStyle = MUTED;
  ctx.font = `italic 16px ${EXPORT_FONT}`;
  ctx.fillText(straights.label, PAD, y - ROW / 2);
  straights.cells.forEach((cell, i) => {
    const x = PAD + LABEL_W + i * colW;
    drawBar(ctx, x, y - ROW / 2, cell.value, maxAbs);
    ctx.fillStyle = deltaColor(cell.value);
    ctx.font = `italic 16px ${EXPORT_FONT}`;
    ctx.fillText(cell.text, x + BAR_W + 12, y - ROW / 2);
  });
  ctx.strokeStyle = BORDER;
  ctx.beginPath(); ctx.moveTo(PAD, y + 6); ctx.lineTo(W - PAD, y + 6); ctx.stroke();
  y += 12;

  // Signature strip
  y += 12;
  for (const s of signature) {
    y += 30;
    chip(ctx, PAD, y - 15, s.accent, 11);
    ctx.fillStyle = BRIGHT;
    ctx.font = `600 15px ${EXPORT_FONT}`;
    ctx.fillText(s.name, PAD + 22, y - 15);
    let sx = PAD + 240;
    ctx.font = `15px ${EXPORT_FONT}`;
    for (const st of s.stats) {
      ctx.fillStyle = MUTED;
      ctx.fillText(`${st.label} `, sx, y - 15);
      sx += ctx.measureText(`${st.label} `).width;
      ctx.fillStyle = TEXT;
      ctx.font = `600 15px ${EXPORT_FONT}`;
      ctx.fillText(st.value, sx, y - 15);
      sx += ctx.measureText(st.value).width + 26;
      ctx.font = `15px ${EXPORT_FONT}`;
    }
  }

  if (watermark) {
    ctx.fillStyle = '#5a6672';
    ctx.font = `14px ${EXPORT_FONT}`;
    ctx.textAlign = 'right';
    ctx.fillText(watermark, W - PAD, height - 18);
    ctx.textAlign = 'left';
  }

  await downloadCanvasPng(canvas, filename);
}
